import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import { useConnectModal } from "@/context/ConnectModalContext";

const LADDER = ["Unknown", "Visible", "Recognised", "Trusted", "Sought after"];

const Ladder = () => (
  <Reveal delay={150}>
    <ol className="relative pl-10" aria-label="The Arise and Echo progression" data-testid="ae-ladder">
      <span className="absolute left-[11px] top-3 bottom-3 w-[3px] rounded-full" aria-hidden="true"
        style={{ background: "linear-gradient(180deg,#c9bfae 0%,var(--arm-ae-gold) 55%,var(--arm-ae) 100%)" }} />
      {LADDER.map((l, i) => {
        const last = i === LADDER.length - 1;
        return (
          <li key={l} className="relative py-3.5">
            <span className="absolute -left-10 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-[var(--arm-ae-tint)]"
              style={{ background: last ? "var(--arm-ae)" : i === 0 ? "#b9ae9b" : "var(--arm-ae-gold)", boxShadow: last ? "0 0 0 8px rgba(180,53,42,.12), 0 0 28px 6px rgba(200,145,43,.45)" : "none" }} aria-hidden="true" />
            <span className={`font-display ${last ? "text-4xl text-[var(--arm-ae)]" : "text-3xl text-hop-blue"} ${i === 0 ? "opacity-60" : ""}`}>{l}</span>
          </li>
        );
      })}
    </ol>
  </Reveal>
);

const Hero = () => {
  const { openConnectModal } = useConnectModal();
  return (
    <section data-arm="ae" className="hp-hero-light hp-immersive" data-testid="ae-hero" style={{ minHeight: "75vh" }}>
      <img 
        src="/images/ae_visibility_1791101790992.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center 40%' }}
        alt="Visibility background" 
      />
      <div
        className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-white via-white/95 to-white/10"
        aria-hidden="true"
      />
      <div className="hop-container relative z-10 py-16 md:py-24 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="hp-eyebrow hp-eyebrow-bar hp-arm-accent" style={{ color: "var(--arm-ae)" }}>Arise &amp; Echo &middot; Social Media &amp; Visibility Infrastructure</p>
            <h1 className="hp-h1 mt-5">From unheard to unforgettable.</h1>
            <p className="hp-lead mt-7" style={{ color: "#3c4768" }}>
              Talent and expertise do not decide opportunity on their own. Attention does. Arise &amp; Echo, the visibility arm of House of
              Projects, builds the positioning, content and distribution that make a leader, an expert or a company known, trusted and sought after.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button type="button" onClick={openConnectModal} className="hp-btn text-white" style={{ background: "var(--arm-ae)" }}>
                Discuss your visibility <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
              <a href="#proof" className="hp-btn hp-btn-ghost">See the results</a>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5 lg:pl-8">
          <Ladder />
        </div>
      </div>
    </section>
  );
};

const Problem = () => (
  <section className="hp-section" aria-labelledby="ae-problem">
    <div className="hop-container grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead arm="ae" label="The visibility gap" title={<span id="ae-problem">Great talent. No visibility.</span>}
          lead="Millions of experts exist. Very few are known. The people and businesses that are seen get invited, referred and chosen, whether or not they are the best at what they do." />
      </div>
      <div className="lg:col-span-7">
        <Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-md bg-slate-100 p-7">
              <h3 className="hp-h3">Corporate brands</h3>
              <p className="mt-2 font-semibold text-slate-600">High ad spend, low connection.</p>
              <p className="hp-body mt-4 text-[15px]">Attention is rented through paid reach and disappears when the budget does.</p>
            </div>
            <div data-arm="ae" className="rounded-md hp-arm-tint p-7 border border-[var(--arm-ae-gold)]/40">
              <h3 className="hp-h3">Personal brands</h3>
              <p className="mt-2 font-semibold hp-arm-accent">High trust, deep community.</p>
              <p className="hp-body mt-4 text-[15px]">Trust beats targeting. Community beats conversion. People buy from people they feel they know.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Meaning = () => (
  <section className="hp-section" style={{ background: "var(--arm-ae-tint)" }} aria-labelledby="ae-name">
    <div className="hop-container">
      <SectionHead arm="ae" label="Why Arise & Echo" title={<span id="ae-name">Two ideas, one discipline.</span>} />
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        {[["Arise", "Helping people rise into visibility and authority.", "Positioning, presence and the credibility to be taken seriously in a crowded field."],
          ["Echo", "Making a voice, ideas and expertise travel further and last longer.", "Content and distribution that keep working long after a single post is published."]].map(([t, h, d], i) => (
          <Reveal key={t} delay={i * 100}>
            <article data-arm="ae" className="h-full bg-white rounded-md border border-[var(--arm-ae-gold)]/40 p-8 md:p-10">
              <h3 className="font-display text-5xl text-[var(--arm-ae)]">{t}</h3>
              <p className="mt-4 text-xl font-semibold text-hop-blue leading-snug">{h}</p>
              <p className="hp-body mt-3">{d}</p>
            </article>
          </Reveal>
        ))}
      </div>
      <Reveal><p className="mt-12 font-display text-3xl md:text-4xl text-hop-blue max-w-3xl leading-snug">
        We do not simply grow pages. We build people, expertise and companies into recognisable brands.
      </p></Reveal>
    </div>
  </section>
);

const Purpose = () => (
  <section className="hp-section" aria-labelledby="ae-why">
    <div className="hop-container grid lg:grid-cols-12 gap-14 items-start">
      <div className="lg:col-span-5">
        <SectionHead arm="ae" label="Purpose before posting" title={<span id="ae-why">Why before what.</span>}
          lead="This is visibility infrastructure, not a posting schedule. Every engagement begins by settling three questions." />
      </div>
      <Reveal delay={100} className="lg:col-span-7">
        <ul className="border-t border-slate-200">
          {["Why are you showing up?", "What do you stand for?", "What should people remember you for?"].map((q) => (
            <li key={q} className="py-6 border-b border-slate-200 font-display text-3xl text-hop-blue">{q}</li>
          ))}
        </ul>
        <p className="mt-6 font-semibold text-[var(--arm-ae)] text-lg">We do not chase trends. We build relevance.</p>
      </Reveal>
    </div>
  </section>
);

const BUILD = [
  ["Personal brand positioning", "A clear, defensible position for the leader or expert, built on real expertise."],
  ["Content strategy and execution", "Narrative, topics and formats planned deliberately, then produced to a consistent standard."],
  ["End-to-end social media management", "Planning, production and distribution run as one managed workflow."],
  ["Community building", "An engaged audience of the right people, not an anonymous follower count."],
];

const Build = () => (
  <section className="hp-section hp-tint" aria-labelledby="ae-build">
    <div className="hop-container">
      <SectionHead arm="ae" label="What we build" title={<span id="ae-build">Personal brand building through strategic content.</span>}
        lead="Across Instagram, YouTube and LinkedIn, around the expertise that already exists." />
      <Reveal><dl className="mt-12 grid md:grid-cols-2 gap-x-12">
        {BUILD.map(([t, d]) => (
          <div key={t} className="py-6 border-t border-slate-300">
            <dt className="hp-h3">{t}</dt>
            <dd className="hp-body mt-2 text-[15px]">{d}</dd>
          </div>
        ))}
      </dl></Reveal>
    </div>
  </section>
);

const FORMATS = [
  ["Short-form", "Attention", "Quick, engaging video that captures attention and expands visibility."],
  ["Carousels", "Authority and education", "Swipeable, in-depth guides that establish expertise and teach."],
  ["Personal and brand content", "Human connection", "Authentic glimpses of the person behind the expertise, to build trust and relatability."],
  ["Long-form", "Depth and credibility", "Detailed conversations, podcasts and articles for deeper engagement and lasting value."],
];
const PATH = ["Short-form", "Deeper engagement", "Long-form", "Thought leadership", "Authority", "Commercial opportunity"];

const Formats = () => (
  <section className="hp-section relative overflow-hidden bg-[#f7f7f5]" aria-labelledby="ae-formats">
    {/* Background Image with strong readable gradient fade */}
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
      <img
        src="/images/ae_visibility_1791101790992.jpg"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#f7f7f5] via-[#f7f7f5]/90 to-[#f7f7f5]/10" />
    </div>
    
    <div className="hop-container relative" style={{ zIndex: 10 }}>
      <SectionHead arm="ae" label="The content system" title={<span id="ae-formats">Not just content. Credibility.</span>}
        lead="Every format has a strategic job. We assign each one deliberately." />
      <Reveal><div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {FORMATS.map(([t, role, d], i) => (
          <article key={t} data-arm="ae" className="rounded-md border border-slate-200/60 bg-white/40 backdrop-blur-md overflow-hidden shadow-sm">
            <div className="h-1.5" style={{ background: ["#e07a3f", "var(--arm-ae-gold)", "#cf5a4a", "var(--arm-ae)"][i] }} />
            <div className="p-6">
              <h3 className="hp-h3">{t}</h3>
              <p className="mt-1 text-sm font-bold hp-arm-accent">{role}</p>
              <p className="hp-body mt-3 text-[15px]">{d}</p>
            </div>
          </article>
        ))}
      </div></Reveal>

      <Reveal><div className="mt-16 bg-white/40 backdrop-blur-md p-6 rounded-lg border border-slate-100/60 shadow-sm">
        <h3 className="font-display text-3xl text-hop-blue max-w-2xl">Short-form builds attention. Long-form builds legacy.</h3>
        <p className="hp-body mt-3 max-w-2xl">Instagram earns initial traction, YouTube deepens engagement, and podcasts carry thought leadership. Each stage sets up the next.</p>
        <ol className="mt-8 flex flex-col md:flex-row md:items-stretch" aria-label="Content progression">
          {PATH.map((p, i) => (
            <li key={p} data-arm="ae" className="flex-1 relative px-5 py-4 text-[15px] font-bold text-hop-blue border border-[var(--arm-ae-gold)]/50 -mt-px md:mt-0 md:-ml-px first:rounded-t-md md:first:rounded-l-md md:first:rounded-tr-none last:rounded-b-md md:last:rounded-r-md md:last:rounded-bl-none"
              style={{ background: `rgba(200,145,43,${0.05 + i * 0.07})` }}>{p}</li>
          ))}
        </ol>
      </div></Reveal>
    </div>
  </section>
);

const STEPS = [
  ["Monthly strategy discussions", "Define goals, themes and content direction."],
  ["Topic and narrative planning", "Develop engaging stories and the key messages behind them."],
  ["Dedicated shoot day", "Efficiently create high-quality visuals and video in one focused session."],
  ["Strategic posting and distribution", "Maximise reach across platforms at the right times."],
];

const Process = () => (
  <section className="hp-section hp-ink" aria-labelledby="ae-process">
    <div className="hop-container">
      <SectionHead dark arm="ae" label="How it runs" title={<span id="ae-process">Simple. Consistent. Strategic.</span>}
        lead="A repeatable monthly rhythm that keeps strategy, production and distribution connected." />
      <Reveal><ol className="mt-12 grid md:grid-cols-4 gap-px bg-white/15 border border-white/15 rounded-md overflow-hidden">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="bg-[var(--hop-blue)] p-7">
            <span className="font-display text-5xl text-[var(--arm-ae-gold)]">{i + 1}</span>
            <h3 className="font-display text-2xl text-white mt-3 leading-tight">{t}</h3>
            <p className="mt-3 text-[15px] text-slate-300 leading-relaxed">{d}</p>
          </li>
        ))}
      </ol></Reveal>
    </div>
  </section>
);

const CASES = [
  {
    name: "Luxury & hospitality founder", role: "Client under NDA", focus: "Social media growth on Instagram",
    stats: [["18K+", "followers gained in 3.5 months"], ["5.7M+", "views in 3.5 months"]],
    points: ["Inbound wedding enquiries", "Outreach from a luxury brand", "Elite followers and viewers"],
  },
  {
    name: "Agri-biotech founder", role: "Client under NDA", focus: "Thought leadership through long-form video on YouTube",
    stats: [["5K+", "followers gained in 3 months"], ["1M+", "views within 3 months"]],
    points: ["Authority built via YouTube", "Content-driven lead conversion, with 30+ inbound leads for farm setup", "Supported brand positioning and visibility ahead of a major corporate milestone"],
  },
  {
    name: "Industrial group director", role: "Client under NDA", focus: "Thought leadership through podcast-led long-form video",
    stats: [["7K+", "subscribers gained in 6 months"], ["1M+", "long-form views in 6 months"]],
    points: ["Authority built through podcasts", "Top industry voices featured", "High-credibility guest conversations", "Organic long-form growth"],
  },
];

const Proof = () => (
  <section id="proof" className="hp-section scroll-mt-24" style={{ background: "var(--arm-ae-tint)" }} aria-labelledby="ae-proof" data-testid="ae-proof">
    <div className="hop-container">
      <SectionHead arm="ae" label="Selected results" title={<span id="ae-proof">What visibility has meant for leaders we have worked with.</span>}
        lead="Each engagement was built around the individual's expertise and goals. We work under NDA, so names stay private and your personal brand is managed discreetly. These are the numbers, not a promise of yours." />
      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        {CASES.map((c, i) => (
          <Reveal key={c.name} delay={i * 90}>
            <article data-arm="ae" className="h-full bg-white rounded-md border border-[var(--arm-ae-gold)]/40 flex flex-col">
              <div className="p-7 border-b border-slate-100">
                <h3 className="hp-h3">{c.name}</h3>
                <p className="text-[15px] font-semibold text-slate-600 mt-1">{c.role}</p>
                <p className="text-sm mt-3 hp-arm-accent font-bold">{c.focus}</p>
              </div>
              <dl className="grid grid-cols-2 border-b border-slate-100">
                {c.stats.map(([n, l]) => (
                  <div key={n + l} className="p-6 first:border-r border-slate-100">
                    <dt className="font-display text-4xl text-hop-blue">{n}</dt>
                    <dd className="text-[13.5px] text-slate-600 mt-1 leading-snug">{l}</dd>
                  </div>
                ))}
              </dl>
              <ul className="p-7 space-y-2.5 flex-1">
                {c.points.map((p) => (
                  <li key={p} className="text-[15px] text-slate-700 leading-snug pl-4 relative before:absolute before:left-0 before:top-[.6em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[var(--arm-ae)]">{p}</li>
                ))}
              </ul>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const OUTCOMES = [
  ["Brand collaborations", "Partnerships with industry leaders and brands."],
  ["Exclusive invitations", "Access to events, platforms and networks."],
  ["Authority and recognition", "Becoming a trusted voice in your industry."],
  ["Inbound opportunity", "Unsolicited speaking requests, enquiries and commercial conversations."],
];

const Outcomes = () => (
  <section className="hp-section" aria-labelledby="ae-out">
    <div className="hop-container grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead arm="ae" label="What visibility really brings" title={<span id="ae-out">Outcomes beyond followers.</span>}
          lead="Follower counts are a by-product. The value is in trust, community and the commercial conversations that follow." />
        <Reveal delay={100}><p className="mt-6 text-sm text-slate-500 max-w-md">These are potential outcomes of effective visibility infrastructure. Results vary by individual, industry and time, and none are guaranteed.</p></Reveal>
      </div>
      <Reveal delay={100} className="lg:col-span-7">
        <dl className="grid sm:grid-cols-2 gap-5">
          {OUTCOMES.map(([t, d]) => (
            <div key={t} data-arm="ae" className="rounded-md border border-slate-200 p-6">
              <div className="hp-arm-bar h-1 w-10 rounded" />
              <dt className="hp-h3 mt-4">{t}</dt>
              <dd className="hp-body mt-2 text-[15px]">{d}</dd>
            </div>
          ))}
        </dl>
      </Reveal>
    </div>
  </section>
);

const PRINCIPLES = ["No shortcuts, no gimmicks", "Long-term brand thinking", "A community-first approach", "Sustainable growth"];

const Principles = () => {
  const { openConnectModal } = useConnectModal();
  return (
    <section className="hp-section" style={{ background: "linear-gradient(180deg,#fdf8ef,#fbf1e4)" }} aria-labelledby="ae-pr">
      <div className="hop-container">
        <SectionHead arm="ae" label="Our approach" title={<span id="ae-pr">Built on strategy. Known for results.</span>} />
        <Reveal><ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRINCIPLES.map((p) => <li key={p} className="font-display text-2xl text-hop-blue border-t-2 border-[var(--arm-ae)] pt-4">{p}</li>)}
        </ul></Reveal>
        <Reveal><div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between border-t border-[var(--arm-ae-gold)]/50 pt-8">
          <p className="font-display text-2xl text-hop-blue max-w-xl">Arise &amp; Echo is one of three capabilities within <Link to="/" className="underline decoration-[var(--arm-ae)] underline-offset-4">House of Projects</Link>.</p>
          <button type="button" onClick={openConnectModal} className="hp-btn text-white shrink-0" style={{ background: "var(--arm-ae)" }}>
            Discuss your visibility <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div></Reveal>
      </div>
    </section>
  );
};

export default function AriseEcho() {
  return (
    <>
      <Seo title="Arise & Echo: Social Media & Visibility Infrastructure"
        description="Arise & Echo builds visibility infrastructure for leaders, experts and companies: positioning, content strategy, production and distribution that earn recognition, trust and inbound opportunity."
        path="/arise-and-echo" />
      <Hero /><Problem /><Meaning /><Purpose /><Build /><Formats /><Process /><Proof /><Outcomes /><Principles />
    </>
  );
}
